import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  Kanban, 
  Upload, 
  BarChart3, 
  Settings,
  LogOut,
  Menu,
  X,
  Phone,
  ChevronRight,
  Trophy,
  BookOpen,
  UserCog,
  Inbox,
  Key
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import QuickLogModal from '@/components/QuickLogModal';

// Navigation items with role-based visibility
const getNavItems = (isAdmin, isSetter) => {
  const items = [
    { path: '/', label: 'Today', icon: LayoutDashboard, roles: ['owner', 'setter'] },
    { path: '/leads', label: 'Leads', icon: Users, roles: ['owner', 'setter'] },
    { path: '/pipeline', label: 'Pipeline', icon: Kanban, roles: ['owner', 'setter'] },
    { path: '/scoreboard', label: 'Scoreboard', icon: Trophy, roles: ['owner', 'setter'] },
    { path: '/scripts', label: 'Scripts', icon: BookOpen, roles: ['owner', 'setter'] },
    { path: '/import', label: 'Import', icon: Upload, roles: ['owner'] }, // Admin only
    { path: '/reports', label: 'Reports', icon: BarChart3, roles: ['owner'] }, // Admin only (full reports)
    { path: '/unassigned', label: 'Unassigned', icon: Inbox, roles: ['owner'] }, // Admin only
    { path: '/settings', label: 'Settings', icon: Settings, roles: ['owner'] }, // Admin only
  ];

  return items.filter(item => {
    if (isAdmin) return true;
    if (isSetter) return item.roles.includes('setter');
    return false;
  });
};

const Layout = ({ children }) => {
  const { user, logout, isOwner, isAdmin, isSetter } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quickLogOpen, setQuickLogOpen] = useState(false);

  const navItems = getNavItems(isAdmin, isSetter);

  const NavItem = ({ item, mobile = false }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    
    return (
      <NavLink
        to={item.path}
        onClick={() => mobile && setMobileMenuOpen(false)}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
          isActive 
            ? 'bg-primary/10 text-primary border border-primary/20' 
            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
        }`}
        data-testid={`nav-${item.label.toLowerCase()}`}
      >
        <Icon className="w-5 h-5" />
        <span className="font-medium">{item.label}</span>
        {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 h-full w-64 flex-col bg-card border-r border-border z-40">
        <div className="p-6">
          <h1 className="text-2xl font-bold text-foreground tracking-tight">
            Lumero<span className="text-primary">CRM</span>
          </h1>
          <p className="text-xs text-muted-foreground mt-1">AI Consultancy Sales</p>
        </div>
        
        <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
          {navItems.map(item => (
            <NavItem key={item.path} item={item} />
          ))}
        </nav>
        
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-4">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-primary/20 text-primary">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <div className="flex items-center gap-2">
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                <Badge variant={isAdmin ? "default" : "secondary"} className="text-[10px] px-1.5">
                  {user?.role}
                </Badge>
              </div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            onClick={() => navigate('/change-password')}
            data-testid="change-password-link"
          >
            <Key className="w-4 h-4 mr-2" />
            Change Password
          </Button>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-muted-foreground hover:text-destructive"
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-40 flex items-center justify-between px-4">
        <h1 className="text-xl font-bold">
          Lumero<span className="text-primary">CRM</span>
        </h1>
        
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            className="bg-primary hover:bg-primary/90"
            onClick={() => setQuickLogOpen(true)}
            data-testid="mobile-quick-log-btn"
          >
            <Phone className="w-4 h-4 mr-1" />
            Log
          </Button>
          
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-card border-l border-border p-0">
              <div className="p-6 border-b border-border">
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-primary/20 text-primary text-lg">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold">{user?.name}</p>
                    <Badge variant={isAdmin ? "default" : "secondary"} className="text-xs">
                      {user?.role}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <nav className="p-4 space-y-1">
                {navItems.map(item => (
                  <NavItem key={item.path} item={item} mobile />
                ))}
              </nav>
              
              <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
                <Button 
                  variant="ghost" 
                  className="w-full justify-start text-muted-foreground hover:text-destructive"
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-40 mobile-nav">
        <div className="flex justify-around items-center py-2">
          {navItems.slice(0, 5).map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center p-2 rounded-lg transition-colors ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-[10px] mt-1">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Main Content */}
      <main className="md:ml-64 pt-16 md:pt-0 pb-20 md:pb-0 min-h-screen">
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>

      {/* Quick Log Modal */}
      <QuickLogModal 
        open={quickLogOpen} 
        onOpenChange={setQuickLogOpen}
      />
    </div>
  );
};

export default Layout;
